import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import "../styles/welcomeBannerPopup.css";

interface WelcomeBanner {
  id: number;
  title: string;
  image: string;
  rendering_page_link: string;
  link: string;
  priority: number;
  start_date: string;
  end_date: string;
  status: string;
}

export default function WelcomeBannerPopup() {
  const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const IMAGE_URL = import.meta.env.VITE_LYRAZONE_IMAGE_URL;

  const location = useLocation();

  const [banner, setBanner] = useState<WelcomeBanner | null>(null);
  const [closedPage, setClosedPage] = useState<string | null>(null);

  const currentPageLink = `${location.pathname}${location.search}`;

  const normalizeTargetLink = (link?: string) => {
    if (!link) return "";

    const cleanLink = String(link).trim();

    if (!cleanLink) return "";

    if (cleanLink.startsWith("http://") || cleanLink.startsWith("https://")) {
      return cleanLink;
    }

    if (cleanLink.startsWith("/")) {
      return cleanLink;
    }

    return `/${cleanLink}`;
  };

  const handleBannerClick = () => {
    if (!banner?.link) return;

    const targetLink = normalizeTargetLink(banner.link);

    if (!targetLink) return;

    window.location.href = targetLink;
  };

  useEffect(() => {
    let isMounted = true;

    const fetchWelcomeBanner = async () => {
      try {
        setBanner(null);

        const res = await fetch(
          `${BASE_URL}/admin/welcome-banners/active?pageLink=${encodeURIComponent(
            currentPageLink,
          )}`,
          {
            credentials: "include",
          },
        );

        const result = await res.json();

        if (!isMounted) return;

        if (
          result.success &&
          Array.isArray(result.data) &&
          result.data.length
        ) {
          const firstBanner = result.data.find((item: WelcomeBanner) => {
            return Boolean(item.image);
          });

          setBanner(firstBanner || null);
        } else {
          setBanner(null);
        }
      } catch (error) {
        console.error("Fetch Welcome Banner Error:", error);

        if (isMounted) {
          setBanner(null);
        }
      }
    };

    if (closedPage !== currentPageLink) {
      fetchWelcomeBanner();
    }

    return () => {
      isMounted = false;
    };
  }, [BASE_URL, currentPageLink, closedPage]);

  if (!banner || closedPage === currentPageLink) {
    return null;
  }

  return (
    <div className="welcome-banner-overlay">
      <div className="welcome-banner-box">
        <button
          type="button"
          className="welcome-banner-close"
          onClick={() => setClosedPage(currentPageLink)}
          aria-label="Close welcome banner"
        >
          ×
        </button>

        <div className="welcome-banner-image-frame">
          <img
            src={`${IMAGE_URL}/uploads/welcomeBanner/${banner.image}`}
            alt={banner.title || "Welcome Banner"}
            className={`welcome-banner-image ${
              banner.link ? "welcome-banner-image-clickable" : ""
            }`}
            onClick={handleBannerClick}
          />
        </div>
      </div>
    </div>
  );
}
